import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PriceChart from "@/components/PriceChart";
import { Share, Copy, Trophy, ArrowLeft, Robot, UserPlus, UserMinus, Sparkle, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";

const STRATEGY_LABEL = {
  ma_crossover: "MA Crossover",
  rsi: "RSI",
  macd: "MACD",
  bollinger: "Bollinger",
};

function buildCopyHref(detail) {
  const p = new URLSearchParams({
    copy: "1",
    symbol: detail.symbol,
    strategy: detail.strategy,
    from_bot: detail.bot_id,
  });
  Object.entries(detail.params || {}).forEach(([k, v]) => {
    if (v !== null && v !== undefined) p.set(k, String(v));
  });
  return `/bots?${p.toString()}`;
}

export default function PublicBotPage() {
  const { botId } = useParams();
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [followStatus, setFollowStatus] = useState(null); // {following, self, owner_tag}
  const [followBusy, setFollowBusy] = useState(false);
  const [explain, setExplain] = useState(null); // string
  const [explainLoading, setExplainLoading] = useState(false);
  const [explainError, setExplainError] = useState(null);

  useEffect(() => {
    api
      .get(`/leaderboard/public/${botId}`)
      .then((r) => setDetail(r.data))
      .catch((e) => {
        if (e.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [botId]);

  // Follow status: only relevant for authenticated users
  useEffect(() => {
    if (!user || !botId) return;
    api.get(`/follows/status/${botId}`).then((r) => setFollowStatus(r.data)).catch(() => {});
  }, [user, botId]);

  const toggleFollow = async () => {
    if (!user) {
      toast.info("Sign in to follow traders");
      return;
    }
    if (followBusy || !followStatus || followStatus.self) return;
    setFollowBusy(true);
    try {
      if (followStatus.following) {
        await api.delete(`/follows/${botId}`);
        setFollowStatus({ ...followStatus, following: false });
        toast.success(`Unfollowed ${followStatus.owner_tag}`);
      } else {
        const r = await api.post("/follows", { bot_id: botId });
        setFollowStatus({ ...followStatus, following: true, owner_tag: r.data.owner_tag });
        toast.success(`Now following ${r.data.owner_tag}`);
      }
    } catch (e) {
      toast.error("Could not update follow — please retry");
    } finally {
      setFollowBusy(false);
    }
  };

  const requestExplain = async () => {
    if (explainLoading) return;
    setExplainLoading(true);
    setExplainError(null);
    try {
      const r = await api.post(`/leaderboard/public/${botId}/explain`);
      setExplain(r.data.text);
    } catch (e) {
      setExplainError(e.response?.data?.detail || "Could not generate explanation");
    } finally {
      setExplainLoading(false);
    }
  };

  const share = async () => {
    if (!detail) return;
    const url = window.location.href;
    api.post(`/leaderboard/share/${detail.bot_id}`).catch(() => {});
    try {
      if (navigator.share) {
        await navigator.share({
          title: `STRATOS — ${detail.symbol} ${detail.strategy}`,
          text: `${detail.symbol} ${detail.strategy} bot — ${detail.win_rate}% win rate on STRATOS`,
          url,
        });
        toast.success("Shared");
        return;
      }
    } catch (_) {}
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Profile link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center mono text-xs text-zinc-500">
        Loading strategy…
      </div>
    );
  }

  if (notFound || !detail) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-4">
        <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">404</div>
        <div className="heading text-3xl font-black tracking-tighter">Strategy not found</div>
        <Link
          to="/leaderboard"
          className="mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white border border-zinc-800 rounded-sm px-4 py-2"
        >
          ← Browse the leaderboard
        </Link>
      </div>
    );
  }

  const positive = detail.total_realized_pl >= 0;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="public-bot-page">
      {/* Top bar */}
      <header className="border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/leaderboard" className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white flex items-center gap-2">
            <ArrowLeft size={12} /> Leaderboard
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center bg-[#00C805] rounded-sm">
              <span className="text-black font-black text-xs">S</span>
            </div>
            <span className="heading font-black tracking-tighter">STRATOS</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-8">
        {/* Hero */}
        <div className="flex items-start justify-between flex-wrap gap-6">
          <div>
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest flex items-center gap-2">
              <Trophy size={12} className="text-[#00C805]" /> Public strategy · by{" "}
              <Link
                to={`/t/${detail.owner_tag}`}
                data-testid="public-bot-owner-link"
                className="text-zinc-300 hover:text-white hover:underline"
              >
                {detail.owner_tag}
              </Link>
            </div>
            <h1 className="heading text-5xl font-black tracking-tighter mt-3">{detail.symbol}</h1>
            <div className="mono text-sm text-zinc-400 uppercase tracking-widest mt-2">
              {STRATEGY_LABEL[detail.strategy] || detail.strategy}
              <span className="text-zinc-600 mx-2">·</span>
              {detail.quantity} share{detail.quantity === 1 ? "" : "s"} / signal
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user && followStatus && !followStatus.self && (
              <button
                onClick={toggleFollow}
                disabled={followBusy}
                data-testid="public-bot-follow-btn"
                className={`inline-flex items-center gap-1.5 text-sm rounded-sm px-3 py-2 mono text-[10px] uppercase tracking-widest transition-colors ${
                  followStatus.following
                    ? "text-white border border-zinc-700 hover:border-zinc-500"
                    : "text-black bg-white hover:bg-zinc-200 border border-white"
                }`}
              >
                {followStatus.following ? <UserMinus size={12} /> : <UserPlus size={12} weight="bold" />}
                {followStatus.following ? "Following" : "Follow"}
              </button>
            )}
            <button
              onClick={share}
              data-testid="public-bot-share-btn"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-300 hover:text-white border border-zinc-800 hover:border-zinc-500 rounded-sm px-3 py-2 mono text-[10px] uppercase tracking-widest"
            >
              <Share size={12} /> Share
            </button>
            <Link
              to={buildCopyHref(detail)}
              data-testid="public-bot-copy-btn"
              className="inline-flex items-center gap-1.5 text-sm text-black bg-[#00C805] hover:bg-[#00b004] rounded-sm px-4 py-2 mono text-[10px] uppercase tracking-widest font-semibold"
            >
              <Copy size={12} weight="bold" /> Copy this strategy
            </Link>
          </div>
        </div>

        {/* Tag chips */}
        {detail.tags && detail.tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="public-bot-tags">
            {detail.tags.map((t) => (
              <span
                key={t}
                className="mono text-[10px] text-zinc-400 border border-zinc-800 rounded-sm px-2 py-1 uppercase tracking-widest"
              >
                {t}
              </span>
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="p-4 border border-zinc-800 rounded-sm">
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Realized P/L</div>
            <div className={`mono text-2xl font-bold mt-1.5 ${positive ? "profit" : "loss"}`}>
              {fmtMoney(detail.total_realized_pl, { showSign: true })}
            </div>
          </div>
          <div className="p-4 border border-zinc-800 rounded-sm">
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Win rate</div>
            <div className={`mono text-2xl font-bold mt-1.5 ${detail.win_rate >= 50 ? "profit" : "loss"}`}>
              {detail.num_sells > 0 ? fmtPct(detail.win_rate) : "—"}
            </div>
          </div>
          <div className="p-4 border border-zinc-800 rounded-sm">
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Trades</div>
            <div className="mono text-2xl font-bold mt-1.5">{detail.num_trades}</div>
          </div>
          <div className="p-4 border border-zinc-800 rounded-sm">
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Best · worst</div>
            <div className="mono text-sm mt-2 profit">{detail.best_trade !== 0 ? fmtMoney(detail.best_trade, { showSign: true }) : "—"}</div>
            <div className="mono text-sm loss">{detail.worst_trade !== 0 ? fmtMoney(detail.worst_trade, { showSign: true }) : "—"}</div>
          </div>
          <div className="p-4 border border-zinc-800 rounded-sm">
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Shares</div>
            <div className="mono text-2xl font-bold mt-1.5">{detail.share_count}</div>
          </div>
        </div>

        {/* Price chart */}
        <div>
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest mb-3">
            Live price · {detail.symbol}
          </div>
          <PriceChart symbol={detail.symbol} height={340} />
        </div>

        {/* Params */}
        <div>
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest mb-3">Strategy parameters</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(detail.params || {}).map(([k, v]) => (
              <div key={k} className="p-3 border border-zinc-800 rounded-sm">
                <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">{k.replace(/_/g, " ")}</div>
                <div className="mono text-sm font-semibold mt-1">{v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Explain in plain English (LLM) */}
        <div
          data-testid="public-bot-explain-panel"
          className="border border-zinc-800 rounded-sm p-5 bg-gradient-to-br from-purple-500/[0.03] to-zinc-900"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Sparkle size={16} weight="fill" className="text-purple-400" />
              <div className="mono text-[10px] text-purple-300 uppercase tracking-widest">
                Explain this strategy · plain English
              </div>
            </div>
            {!explain && !explainLoading && (
              <button
                onClick={requestExplain}
                data-testid="public-bot-explain-btn"
                className="inline-flex items-center gap-1.5 text-sm text-white bg-purple-500/20 hover:bg-purple-500/40 border border-purple-500/50 rounded-sm px-3 py-1.5 mono text-[10px] uppercase tracking-widest"
              >
                <Lightning size={12} /> Explain in plain English
              </button>
            )}
          </div>
          {explainLoading && (
            <div className="mt-3 mono text-xs text-zinc-500">Asking the AI…</div>
          )}
          {explainError && (
            <div className="mt-3 mono text-xs text-red-400">{explainError}</div>
          )}
          {explain && (
            <p
              data-testid="public-bot-explain-text"
              className="mt-3 text-sm text-zinc-200 leading-relaxed"
            >
              {explain}
            </p>
          )}
        </div>

        {/* CTA footer */}
        <div className="border border-zinc-800 rounded-sm p-6 flex items-center justify-between flex-wrap gap-4 bg-zinc-900/40">
          <div className="flex items-center gap-3">
            <Robot size={22} className="text-[#00C805]" />
            <div>
              <div className="font-semibold">Trade the same setup — with your own tweaks</div>
              <div className="text-xs text-zinc-500 mt-1">Paper-trading only. Copy loads the params into your workspace so you can tune before going live.</div>
            </div>
          </div>
          <Link
            to={buildCopyHref(detail)}
            className="inline-flex items-center gap-1.5 text-sm text-black bg-[#00C805] hover:bg-[#00b004] rounded-sm px-4 py-2 mono text-[10px] uppercase tracking-widest font-semibold"
          >
            <Copy size={12} weight="bold" /> Copy this strategy
          </Link>
        </div>
      </main>
    </div>
  );
}
